import React from 'react';
import PropTypes from 'prop-types';
import { useApolloClient, useMutation } from '@apollo/client';
import { IconSuccess16, Button, Toast } from '@stitch-fix/mode-react';
import { useSelectEvent } from '@stitch-fix/event-reporter';
import { incrementBagCount } from '../../../helpers/bagHelper';
import onError from '../../../utils/onError';
import { trackSavedItemsAddToBag } from '../../../../../shared/SaveBookmark/SavedItemsTracker';
import { SelectedVariantSavedFragment } from './getGatedMoveToBag.graphql';
import SavedItemMoveToBag from './SavedItemMoveToBagQuery.graphql';
import ClickTooltip from '../../../../../shared/ClickTooltip/ClickTooltip';
import {
  allowClick,
  unselectedStateMessage,
} from '../../../../../../helpers/unselectedStateHelper';

import './MoveToBag.module.scss';
import { useProductDetailsPageContext } from '../../../../../ProductDetailsPage/ProductDetailsPageContext/ProductDetailsPageContext';

const MoveToBag = ({ label = 'Move to bag' }) => {
  const [isDisabled, setIsDisabled] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);
  const [showToast, setShowToast] = React.useState(false);
  const [state, dispatch] = useProductDetailsPageContext();

  const skuId = state.selectedSkuId;
  const externalProductId = state.externalProductId || '';

  const client = useApolloClient();
  const { reportSelectEvent } = useSelectEvent();

  const onBagError = (title, message) => {
    dispatch({
      type: 'SET_SHOPPING_BAG_ERROR',
      payload: {
        bagError: {
          bagErrorTitle: title,
          bagErrorMessage: message,
        },
      },
    });
  };

  const setUnsavedInCache = () => {
    // Moving to bag will also unsave the item, so we update the apollo cache:
    client.writeFragment({
      id: `ShopProductVariant:${skuId}`,
      fragment: SelectedVariantSavedFragment,
      fragmentName: 'SelectedVariantSavedFragment',
      data: {
        saved: false,
      },
    });
  };

  const [moveToBag] = useMutation(SavedItemMoveToBag, {
    onCompleted: ({ savedItemMoveToBag }) => {
      const { result } = savedItemMoveToBag;

      const resultState = 'id' in result ? 'SUCCESS' : result.code;

      switch (resultState) {
        case 'SUCCESS':
          setShowToast(true);
          incrementBagCount();
          setUnsavedInCache();
          trackSavedItemsAddToBag({
            skuId,
            canonicalName: 'pdp',
            savedCarousel: false,
            productEventsContext: {
              source: state.source,
              subSource: state.subSource,
              collection: state.collectionName,
            },
            externalProductId,
          });
          break;
        case 'BAG_FULL':
          onBagError(
            'Oops, you’ve reached the item limit',
            'Your bag is full. Remove items to add more.',
          );
          break;
        default:
          setHasError(true);
          onError(result.code);
          onBagError(
            'Oops, this item wasn’t added',
            'There was an error adding this item to your bag.',
          );
          break;
      }
      setIsDisabled(false);
    },
    onError: () => {
      onBagError(
        'Oops, this item wasn’t added',
        'There was an error adding this item to your bag.',
      );
      setIsDisabled(false);
    },
  });

  const buttonText = () => {
    if (isDisabled && !hasError) {
      return 'Moving to bag...';
    }

    return label;
  };

  const onMoveToBag = () => {
    setIsDisabled(true);

    reportSelectEvent({
      schema: 'sku_select',
      name: 'add_to_bag',
      action_name: 'add_to_bag',
      sku_id: skuId,
      external_product_id: externalProductId,
    });

    moveToBag({
      variables: { input: { sku: { id: skuId } } },
    });
  };

  return allowClick(state) ? (
    <div styleName="container">
      <Button
        // Needed to override min-width setting in button for small screen use
        // case, so two buttons can fit side-by-side on small screen widths.
        // eslint-disable-next-line @stitch-fix/no-dangerous-mode-react-styling-props
        dangerouslySetStyleName="button"
        disabled={isDisabled}
        onClick={onMoveToBag}
        type="submit"
        name="commit"
        variant="filled-statement"
        data-testing-id="move-to-bag-button"
        width="fill"
      >
        {buttonText()}
      </Button>
      <Toast
        isOpen={showToast}
        onClose={() => setShowToast(false)}
        startContent={<IconSuccess16 purpose="decorative" />}
      >
        Item moved to your bag.
      </Toast>
    </div>
  ) : (
    <ClickTooltip
      text={unselectedStateMessage(state)}
      show={
        !allowClick(state) && state.ctaTooltipDisplay?.showAddToBag === true
      }
      arrowDirection="down"
    >
      <Button
        width="fill"
        aria-label="warning"
        onClick={() => {
          dispatch({
            type: 'DISABLED_ADD_TO_BAG_CLICKED',
          });
        }}
        type="submit"
        name="commit"
        variant="filled-statement"
        data-testid="add-to-bag-button-blocker"
      >
        Move to bag
      </Button>
    </ClickTooltip>
  );
};

MoveToBag.propTypes = {
  label: PropTypes.string,
};

MoveToBag.defaultProps = {
  label: 'Move to bag',
};

export default MoveToBag;
